import React from "react";
import { Heart, Sparkles, Globe, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ExhibitionHeroProps {
  countriesCount: number;
  totalParticipants: number;
  totalBooths: number;
  remainingRequests: number;
  onEnter: () => void;
}

const ExhibitionHero: React.FC<ExhibitionHeroProps> = ({
  countriesCount,
  totalParticipants,
  totalBooths,
  remainingRequests,
  onEnter,
}) => {
  return (
    <div className="flex flex-col items-center px-6 py-6">
      {/* Animated Hero Icon */}
      <div className="relative mb-5">
        {/* Floating hearts - slow, dreamy movement */}
        <div className="absolute -left-10 -top-2 float-animation">
          <Heart className="w-7 h-7 text-pink-400 fill-pink-400 opacity-80" />
        </div>
        <div className="absolute -right-8 top-6 float-animation-delayed">
          <Heart className="w-5 h-5 text-rose-400 fill-rose-400 opacity-70" />
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 -top-6 float-animation-slow">
          <Sparkles className="w-5 h-5 text-amber-400 opacity-90" />
        </div>
        <div className="absolute -left-6 bottom-0 float-animation" style={{ animationDelay: "0.5s" }}>
          <Heart className="w-4 h-4 text-pink-300 fill-pink-300 opacity-60" />
        </div>
        <div className="absolute -right-4 -bottom-2 float-animation-delayed" style={{ animationDelay: "0.8s" }}>
          <Sparkles className="w-4 h-4 text-rose-300 opacity-70" />
        </div>

        {/* Main globe with shimmer effect */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-400/30 via-rose-500/20 to-red-500/30 blur-xl animate-pulse" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-pink-400 via-rose-500 to-red-500 flex items-center justify-center shadow-2xl overflow-hidden">
            {/* Shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]" />
            <Globe className="w-12 h-12 text-white relative z-10" />
          </div>
        </div>
      </div>

      {/* Title & Subtitle */}
      <h1 className="text-2xl font-bold text-foreground text-center mb-1">
        Love Exhibition Day
      </h1>
      <p className="text-muted-foreground text-center text-sm mb-5">
        Meet people live, city by city
      </p>

      {/* Stats Row - Emotional, Icon-first */}
      <div className="flex items-center justify-center gap-3 mb-5">
        {/* Live indicator - most prominent */}
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-green-500/10 border border-green-500/20">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          <span className="text-lg font-bold text-green-600">{totalParticipants || "–"}</span>
          <span className="text-xs text-green-600/70">live</span>
        </div>

        {/* Countries & Stages - secondary */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-primary/10 border border-primary/20">
          <Globe className="w-4 h-4 text-primary" />
          <span className="font-semibold text-foreground">{countriesCount}</span>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-pop/10 border border-pop/20">
          <Heart className="w-4 h-4 text-pop fill-pop/30" />
          <span className="font-semibold text-foreground">{totalBooths}</span>
        </div>
      </div>

      {/* Primary CTA - Irresistible */}
      <Button
        onClick={onEnter}
        className={cn(
          "relative px-12 py-6 text-lg font-bold rounded-full shadow-2xl",
          "bg-gradient-to-r from-pink-500 via-rose-500 to-red-500",
          "hover:from-pink-600 hover:via-rose-600 hover:to-red-600",
          "text-white overflow-hidden group",
          "pulse-glow hover-lift press-scale"
        )}
      >
        {/* Button shimmer on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <Heart className="w-5 h-5 mr-2 fill-white relative z-10" />
        <span className="relative z-10">Enter Exhibition</span>
      </Button>

      {/* Micro-text under CTA */}
      <p className="text-xs text-muted-foreground/70 mt-2.5 text-center">
        Join live conversations now
      </p>

      {/* Requests remaining - subtle */}
      <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground/60">
        <Users className="w-3.5 h-3.5" />
        <span>{remainingRequests} join requests left today</span>
      </div>
    </div>
  );
};

export default ExhibitionHero;
